import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { CREDIT_CARD } from '../utils/constants';

type RecurlyError = {
  code: number;
  message: string;
  name: string;
};
type PaymentContextType = {
  recurlyToken: string | null;
  setRecurlyToken: Dispatch<SetStateAction<string | null>>;
  billingMethod: string;
  setBillingMethod: Dispatch<SetStateAction<string>>;
  recurlyError: RecurlyError | null;
  setRecurlyError: Dispatch<SetStateAction<RecurlyError | null>>;
  isAltPaymentOpen: boolean;
  setIsAltPaymentOpen: Dispatch<SetStateAction<boolean>>;
  handleApplePay: () => void;
  handlePayPal: () => void;
  handleVenmo: () => void;
};

type PaymentProviderTypes = {
  children?: ReactNode;
};
export const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

// Make sure to use PaymentContext one level up from where you want to consume values
const PaymentProvider: FunctionComponent<PaymentProviderTypes> = ({ children }) => {
  const [recurlyToken, setRecurlyToken] = useState<string | null>(null);
  const [billingMethod, setBillingMethod] = useState<string>(CREDIT_CARD);
  const [recurlyError, setRecurlyError] = useState<RecurlyError | null>(null);
  const [isAltPaymentOpen, setIsAltPaymentOpen] = useState<boolean>(false);

  // apple pay and paypal start functions need to be tied to user gesture
  function handleApplePay() {
    if (window.applePay) {
      window.applePay.begin();
      setIsAltPaymentOpen(true);
    }
  }

  function handlePayPal() {
    if (window.payPal) {
      window.payPal.start();
      setIsAltPaymentOpen(true);
    }
  }

  function handleVenmo() {
    if (window.venmo) {
      window.venmo.start();
      setIsAltPaymentOpen(true);
    }
  }

  return (
    <PaymentContext.Provider
      value={{
        recurlyToken,
        setRecurlyToken,
        billingMethod,
        setBillingMethod,
        recurlyError,
        setRecurlyError,
        handleApplePay,
        handlePayPal,
        handleVenmo,
        isAltPaymentOpen,
        setIsAltPaymentOpen,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export function usePaymentContext() {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('You must use the Payment Provider in order to consume this context.');
  }
  return context;
}

export default PaymentProvider;
